import { lazy, type FC } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { PublicOperatorDetailsResponse } from 'src/apis/organisation-service/types';
import type { ManualPublicOperatorsDetailsRequest } from 'src/apis/organisation-service/use-public-operator-details';
import {
  superBundleAllocationMechanismLabels,
  superBundleQuantityUnitLabels,
} from 'src/apis/super-bundle-service/mappings';
import type { SuperBundleOfferDetails } from 'src/apis/super-bundle-service/types';
import type { SuperBundleOfferDetailsRequest } from 'src/apis/super-bundle-service/use-super-bundle-offer-details';
import type { SuperBundleServiceRequest } from 'src/apis/super-bundle-service/use-super-bundle-service-root';
import { Button } from 'src/components/buttons-and-actions/button';
import { SplitActions } from 'src/components/buttons-and-actions/split-actions';
import { Card } from 'src/components/data-display/card';
import { DataItems, DataList } from 'src/components/data-display/data-list';
import { FormatDate } from 'src/components/data-display/format-date';
import { FormatValue } from 'src/components/data-display/format-value';
import { Markdown } from 'src/components/data-display/markdown';
import { PageHeader } from 'src/components/layout/page-header';
import { Link } from 'src/components/navigation/link';
import { Tooltip } from 'src/components/overlay/tooltip';
import { Heading } from 'src/components/text/heading';
import type { Successful } from 'src/hooks/use-axios';
import { useBreakpoints } from 'src/hooks/use-breakpoints';
import { useModal2 } from 'src/hooks/use-modal';
import { useTitle } from 'src/hooks/use-title';
import { Img } from 'src/pages/settings/organisation/company-information/utils';
import { DeleteOfferModal } from 'src/pages/transport/super-bundle/details/delete-modal';
import {
  OfferNotFound,
  OfferStatusWithTimer,
  OperatorProposals,
} from 'src/pages/transport/super-bundle/utils';

const BidSection = lazy(() => import('./bids/page'));
type Props = {
  superBundleServiceRoot: Successful<SuperBundleServiceRequest>;
  superBundleOfferDetails: Successful<SuperBundleOfferDetailsRequest>;
  manualPublicOperatorsDetails: Successful<ManualPublicOperatorsDetailsRequest>;
};

export const Page: FC<Props> = ({
  superBundleOfferDetails,
  superBundleServiceRoot,
  manualPublicOperatorsDetails,
}) => {
  const { offerId } = useParams<{ offerId?: string }>();
  const offerDetails = superBundleOfferDetails.response.data;

  const operatorsDetails =
    manualPublicOperatorsDetails.response.operatorsDetails;

  const title = offerDetails.name;
  useTitle(title);
  const { md } = useBreakpoints();
  const navigate = useNavigate();

  const canAddInformation =
    !!superBundleOfferDetails.response.data._links.addInformation;
  const deleteOffer = useModal2(
    offerDetails._links.deleteOffer
      ? {
          modal: (props) => (
            <DeleteOfferModal
              offer={offerDetails}
              onSuccess={() => {
                superBundleServiceRoot.refresh?.();
                navigate('/transport/super-bundle');
              }}
              {...props}
            />
          ),
          trigger: (props) => (
            <Button
              data-testid="delete-offer-button"
              aria-label="Delete Offer"
              {...props}
            >
              Delete
            </Button>
          ),
        }
      : null
  );

  if (offerId !== offerDetails.offerId) {
    return <OfferNotFound superBundleServiceRoot={superBundleServiceRoot} />;
  }

  return (
    <div
      className={`
        grid items-center gap-xl
        legacy:gap-[2rem]
      `}
    >
      {deleteOffer?.modal}
      <PageHeader
        title={title}
        status={
          <OfferStatusWithTimer
            status={offerDetails.offerStatus}
            countdownEnd={offerDetails.datetimeOfNextStatusSwitch}
            superBundleMarketingRoot={superBundleServiceRoot}
          />
        }
        actions={
          <SplitActions mode="button-primary" testId="lng-offer-details">
            {canAddInformation && (
              <Link
                data-testid="add-information-link"
                to={`/transport/super-bundle/offers/${offerDetails.offerId}/add-information`}
              >
                Add Information
              </Link>
            )}

            {deleteOffer?.trigger}
          </SplitActions>
        }
      />
      <div
        className={`
          grid grid-cols-1 items-start gap-md
          md:grid-cols-2
          legacy:gap-[1rem]
        `}
      >
        <Card>
          <Heading data-testid="product-card" mode="title" size="sm">
            Offer Details
          </Heading>
          <DataList>
            <DataItems>
              <span>Offer Name</span>
              <span data-testid="offerName">{offerDetails.name}</span>
              <span>Offer Publication</span>
              <span data-testid="publication">
                <FormatDate value={offerDetails.publication} type="date-time" />
              </span>
              <span>Capacity Runtime</span>
              <span data-testid="serviceRuntime">
                <FormatDate
                  value={offerDetails.serviceRuntime.start}
                  type="date-time"
                />
                {' - '}
                <FormatDate
                  value={offerDetails.serviceRuntime.end}
                  type="date-time"
                />
              </span>
              <span>Selling Procedure</span>
              <span data-testid="allocationMechanism">
                {
                  superBundleAllocationMechanismLabels[
                    offerDetails.allocationMechanism
                  ]
                }
              </span>
              <span>Bidding Window</span>
              <span data-testid="biddingWindow">
                <FormatDate
                  value={offerDetails.biddingWindow.start}
                  type="date-time"
                />
                {' - '}
                <FormatDate
                  value={offerDetails.biddingWindow.end}
                  type="date-time"
                />
              </span>
              <Tooltip content="The reserve price is the price at which the offer is sold to the shipper.">
                {(targetProps) => <span {...targetProps}>Reserve Price</span>}
              </Tooltip>
              <Tooltip
                maxWidth={64}
                content={
                  <>
                    <DataList>
                      <OperatorProposals
                        proposals={offerDetails.operatorProposals}
                      />
                    </DataList>
                    <span>Exchange updated daily at 15:00.</span>
                  </>
                }
              >
                {(targetProps) => (
                  <span data-testid="reserve-total-price" {...targetProps}>
                    <FormatValue
                      type="exact-decimals"
                      value={offerDetails.reserveTotalPrice}
                      label={`${offerDetails.currency}/${superBundleQuantityUnitLabels[offerDetails.quantityUnit]}`}
                    />
                  </span>
                )}
              </Tooltip>

              {offerDetails.offerStatus === 'CREATED' ? (
                <Tooltip content="The lowest value among all operators’ uploaded capacity.">
                  {(targetProps) => (
                    <span {...targetProps}>Available Amount</span>
                  )}
                </Tooltip>
              ) : (
                <span>Available Amount</span>
              )}
              <span data-testid="availableAmount">
                <FormatValue
                  type="exact-decimals"
                  value={offerDetails.quantity}
                  label={
                    superBundleQuantityUnitLabels[offerDetails.quantityUnit]
                  }
                />
              </span>
            </DataItems>
          </DataList>
        </Card>
        <div
          className={`
            grid items-center gap-md
            legacy:gap-[1rem]
          `}
        >
          <Card>
            <Heading data-testid="operators-card" mode="title" size="sm">
              Operators
            </Heading>
            <DataList>
              <OperatorsList
                proposals={offerDetails.operatorProposals}
                operatorsDetails={operatorsDetails}
              />
            </DataList>
          </Card>

          {offerDetails.description && (
            <Card>
              <Heading data-testid="description-card" mode="title" size="sm">
                Description
              </Heading>
              <div
                data-testid="additional-info-description"
                className="max-h-[22rem] overflow-auto"
              >
                <Markdown content={offerDetails.description} />
              </div>
            </Card>
          )}
        </div>
      </div>

      {(offerDetails._links.placeBid || offerDetails._links.getBids) && (
        <BidSection
          superBundleOfferDetailsRequest={superBundleOfferDetails}
          operatorsDetails={operatorsDetails}
        />
      )}
    </div>
  );
};

const OperatorsList: FC<{
  proposals: SuperBundleOfferDetails['operatorProposals'];
  operatorsDetails: PublicOperatorDetailsResponse[];
}> = ({ proposals, operatorsDetails }) => {
  return proposals.map((proposal) => {
    const operator = operatorsDetails.find((operator) => {
      const operatorId = operator._links.self.href.split('/').pop();
      return operatorId === proposal.operatorId;
    });
    if (!operator) {
      return null;
    }
    return (
      <DataItems key={proposal.operatorId}>
        <span>Name</span>
        <div
          className={`
            grid grid-flow-col items-start justify-between gap-md
            legacy:gap-[1rem]
          `}
        >
          <Link
            to={`/market-information/players/operators/${proposal.operatorId}`}
          >
            {proposal.operatorName}
          </Link>
          <Link
            to={`/market-information/players/operators/${proposal.operatorId}`}
            aria-label={proposal.operatorName}
          >
            <Img
              data-testid="big-logo"
              alt={proposal.operatorName}
              src={operator.smallLogoUrl}
            />
          </Link>
        </div>
      </DataItems>
    );
  });
};
